function getTagNames(tags) {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => (typeof tag === 'string' ? tag : tag.value || tag.name || ''))
      .filter(Boolean);
  }
  return String(tags)
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function normalizeTags(tags) {
  return getTagNames(tags).filter(
    (tag) => tag !== 'they-blog' && !tag.toLowerCase().startsWith('autor:') && !tag.startsWith('#'),
  );
}

function getRelatedSlugs(tags) {
  if (!Array.isArray(tags)) return [];
  return tags
    .filter((tag) => {
      const name = typeof tag === 'string' ? tag : tag.name || '';
      return name.startsWith('#related-');
    })
    .map((tag) => {
      const name = typeof tag === 'string' ? tag : tag.name || '';
      return name.slice('#related-'.length);
    })
    .filter(Boolean);
}

function normalizeArticle(article) {
  const author = article.primary_author || {};
  const tagNames = getTagNames(article.tags);
  const authorTag = tagNames.find((tag) => tag.toLowerCase().startsWith('autor:'));
  const attributedAuthor = authorTag ? authorTag.slice(authorTag.indexOf(':') + 1).trim() : '';

  return {
    slug: article.slug || article._id,
    title: article.title || '',
    excerpt: article.custom_excerpt || article.excerpt || article.summary || '',
    author: attributedAuthor || author.name || article.author || 'they consulting',
    authorRole: author.bio || article.authorRole || article.author_role || '',
    publishedAt: article.published_at || article.publishedAt || article._created || '',
    readingTime:
      typeof article.reading_time === 'number'
        ? `${article.reading_time} Min. Lesezeit`
        : article.readingTime || article.reading_time || '',
    tags: normalizeTags(article.tags),
    relatedSlugs: getRelatedSlugs(article.tags),
    content: article.html || article.content || '',
  };
}

const ghostUrl = process.env.GHOST_URL.replace(/\/$/, '');
const ghostContentApiKey = process.env.GHOST_CONTENT_API_KEY;

async function fetchGhostPosts(slug = null) {
  if (!ghostUrl || !ghostContentApiKey) {
    return [];
  }

  const resource = slug ? `posts/slug/${encodeURIComponent(slug)}/` : 'posts/';
  const params = new URLSearchParams({
    key: ghostContentApiKey,
    include: 'tags,authors',
    formats: 'html,plaintext',
  });

  if (!slug) {
    params.set('limit', 'all');
  }

  const response = await fetch(`${ghostUrl}/ghost/api/content/${resource}?${params.toString()}`, {
    headers: {
      'Accept-Version': 'v6.0',
    },
  });

  if (!response.ok) {
    throw new Error(`Ghost Content API returned ${response.status}.`);
  }

  const payload = await response.json();
  return Array.isArray(payload.posts) ? payload.posts.map(normalizeArticle) : [];
}

async function loadArticles() {
  try {
    return await fetchGhostPosts();
  } catch (error) {
    console.warn('Ghost is not available. No articles will be shown.', error);
    return [];
  }
}

async function loadArticle(slug) {
  try {
    const articles = await fetchGhostPosts(slug);
    return articles[0] || null;
  } catch (error) {
    console.warn('The Ghost article is not available. No article will be shown.', error);
    return null;
  }
}

async function loadArticlesBySlugs(slugs) {
  if (!slugs || !slugs.length || !ghostUrl || !ghostContentApiKey) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      key: ghostContentApiKey,
      include: 'tags,authors',
      formats: 'html,plaintext',
      filter: `slug:[${slugs.join(',')}]`,
      limit: slugs.length.toString(),
    });

    const response = await fetch(`${ghostUrl}/ghost/api/content/posts/?${params.toString()}`, {
      headers: { 'Accept-Version': 'v6.0' },
    });

    if (!response.ok) {
      throw new Error(`Ghost Content API returned ${response.status}.`);
    }

    const payload = await response.json();
    return Array.isArray(payload.posts) ? payload.posts.map(normalizeArticle) : [];
  } catch (error) {
    console.warn('Related articles could not be loaded.', error);
    return [];
  }
}

// If the authors are found on any of the blog posts, their name in the article will be linked
// to their LinkedIn pages.
const authorLinkedIn = {
  // Add entries as: 'Author Name': 'https://www.linkedin.com/in/handle',
  'Lars Quell': 'https://www.linkedin.com/in/lars-quell-791694248/',
  'Florian Dietrichkeit': 'https://www.linkedin.com/in/floriandtk/',
  'Pablo Heimplatz': 'https://www.linkedin.com/in/pabloheimplatz/',
  'Krzysztof Zwierzchlewski': 'https://www.linkedin.com/in/krzysztofzwierzchlewski/',
};

window.TheyBlog = {
  loadArticle,
  loadArticles,
  loadArticlesBySlugs,
  authorLinkedIn,
};
